import { useSnackbar } from '@components/snackbar';
import { LeftArrowIcon, RightArrowIcon, ShrinkArrowIcon, TimerIcon } from '@sketchnote/icons';
import { LoaderSm } from '@sketchnote/shared-components';
import { StopCircle } from '@styled-icons/material';
import cn from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';
import Draggable from 'react-draggable';
import { TimerTracker } from 'src/features/tasks/components/TimeTrackerDetails';
import { truncateString } from 'src/utils/truncateString';

import { Timer, useTimerWidgetStore } from './useTimerWidgetStore';

const TimerItem = ({ timer }: { timer: Timer }) => {
  const { stopTimer } = useTimerWidgetStore((s) => ({
    stopTimer: s.stopTimer,
  }));
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const [openSnackbar] = useSnackbar();

  const ICON_SIZE = 28;

  const onStopTimer = () => {
    stopTimer(timer.task._id, timer.startTime, timer.billable);
    openSnackbar({
      text: `Timer stopped for ${truncateString(timer.task_title, 20)}`,
      snackType: 'standard',
      position: 'bottom-center',
    });
  };

  return (
    <div className="flex border border-gray-200 rounded-lg items-center p-10 mb-6">
      <div className=" flex flex-col bg-gray-100 rounded-lg p-10 mr-12 h-full">
        <span className=" w-92 text-center">
          <TimerTracker
            className=" text-gray-600 font-medium text-sm text-center"
            startTime={timer?.startTime}
            stopTime={timer?.stopTime}
            isRunning={timer?.isRunning}
            timeTrackedTaskId={timer?.task._id}
          />
        </span>
        <div className="w-full flex justify-center mt-4">
          <button
            onClick={onStopTimer}
            disabled={!timer.isRunning}
            className="text-red-400 hover:text-red-600 active:text-red-500"
          >
            <StopCircle size={ICON_SIZE} />
          </button>
        </div>
      </div>
      <div className=" flex flex-col justify-between overflow-hidden">
        <span className="text-xs text-gray-500 truncate-text">{timer.board_title || ''}</span>
        <span className="truncate-text">{timer.task_title || ''}</span>
        <Link href={`/${mode}/${orgId}/tasks/${timer?.task._id}?routeBack=default`}>
          <a>
            <span className="text-primary-600 text-xs">Go to task →</span>
          </a>
        </Link>
      </div>
    </div>
  );
};

export const FloatingTimerWidget = () => {
  const {
    isMinimized,
    minimize,
    timers,
    isLoading,
    selectedTimerIndex,
    setSelectedTimerIndex,
    position,
    setPosition,
    fetchTimers,
  } = useTimerWidgetStore((s) => ({
    isMinimized: s.isMinimized,
    minimize: s.minimize,
    timers: s.timers,
    isLoading: s.isLoading,
    selectedTimerIndex: s.selectedTimerIndex,
    setSelectedTimerIndex: s.setSelectedTimerIndex,
    position: s.position,
    setPosition: s.setPosition,
    fetchTimers: s.fetchTimers,
  }));
  const toggleSwitch = document.getElementById('timer-navbar');
  const modal = document.getElementById('floating-timer');

  const router = useRouter();

  useEffect(() => {
    // TODO: Optimizing to reduce fetchtimer calls
    if (!isMinimized && timers.length === 0 && !!router.query.taskId) {
      fetchTimers();
    }
  }, []);

  const runningTimers = useMemo(() => {
    return timers.filter((timer) => timer.isRunning);
  }, [timers]);

  const onPrevTimerIndex = () => {
    if (selectedTimerIndex === 0) {
      setSelectedTimerIndex(runningTimers.length - 1);
    } else {
      setSelectedTimerIndex(selectedTimerIndex - 1);
    }
  };

  const onNextTimerIndex = () => {
    if (selectedTimerIndex === runningTimers.length - 1) {
      setSelectedTimerIndex(0);
    } else {
      setSelectedTimerIndex(selectedTimerIndex + 1);
    }
  };

  const onStop = (e: any, data: any) => {
    const { x, y } = data;
    setPosition({ x, y });
  };

  const moveModalOnToggle = useCallback(() => {
    const scale = 0.5;
    // Get the position and dimensions of the toggle switch
    if (!toggleSwitch || !modal) return '';
    const toggleSwitchRect = toggleSwitch.getBoundingClientRect();
    const toggleSwitchTop = toggleSwitchRect.top + window.pageYOffset;
    const toggleSwitchLeft = toggleSwitchRect.left + window.pageXOffset;

    // Get the position and dimensions of the modal
    const modalRect = modal.getBoundingClientRect();
    const modalTop = modalRect.top + window.pageYOffset + modalRect.height * scale;
    const modalLeft = modalRect.left + window.pageXOffset + modalRect.width * scale;

    // Calculate the distance between the two elements
    const distanceX = toggleSwitchLeft - modalLeft;
    const distanceY = toggleSwitchTop - modalTop - 50;

    // Apply the CSS transform to move the modal
    return `translate(${distanceX}px, ${distanceY}px) scale(${scale})`;
  }, [isMinimized]);

  // if (isMinimized)
  //   return (
  //     <Draggable position={position}>
  //       <div
  //         id="minimiser"
  //         className="fixed w-320 h-100 bg-gray-150 rounded-lg bottom-20 right-20 z-100 border border-gray-400"
  //       />
  //     </Draggable>
  //   );

  return (
    <Draggable onStop={onStop} position={position} bounds="#__next">
      <div
        id="floating-timer"
        className={cn(' fixed bottom-20 transition-all duration-75 right-20 w-320', {
          'opacity-0 z-0': isMinimized,
          'z-100': !isMinimized,
        })}
      >
        <div
          className={cn(
            'flex flex-col bg-white rounded-lg shadow-md p-8 transition-all duration-200 ease-out',
            {
              'z-0': isMinimized,
            }
          )}
          style={{
            transform: isMinimized ? moveModalOnToggle() : '',
          }}
        >
          <div className="w-full flex justify-between items-center cursor-move">
            <div className="flex-grow">
              {runningTimers.length > 1 && (
                <div className="flex items-center mb-6">
                  <button onClick={onPrevTimerIndex}>
                    <LeftArrowIcon
                      size={25}
                      strokeWidth={2.5}
                      className="p-6 text-gray-600 bg-gray-300 rounded-full cursor-pointer hover:bg-gray-400"
                      wrapperClass="flex"
                    />
                  </button>
                  <span className="text-xss text-gray-500 font-mono mx-6">{`${
                    selectedTimerIndex + 1
                  }/${runningTimers.length}`}</span>
                  <button onClick={onNextTimerIndex}>
                    <RightArrowIcon
                      size={25}
                      strokeWidth={2.5}
                      className="p-6 text-gray-600 bg-gray-300 rounded-full cursor-pointer hover:bg-gray-400"
                      wrapperClass="flex"
                    />
                  </button>
                </div>
              )}
            </div>
            <button onClick={minimize}>
              <ShrinkArrowIcon
                size={25}
                className="p-6 text-gray-600 bg-gray-300 rounded-full mb-6 cursor-pointer hover:bg-gray-400"
              />
            </button>
          </div>
          <div className="relative">
            {isLoading && (
              <div className="absolute top-0 right-0 w-full h-full bg-white opacity-70 p-4 z-100 flex justify-center items-center overflow-hidden">
                <LoaderSm className="loadSize" />
              </div>
            )}
            {/* {runningTimers.map((timer) => (
              <TimerItem key={timer._id} timer={timer} />
            ))} */}
            {runningTimers.length > 0 && (
              <TimerItem
                key={runningTimers[selectedTimerIndex]._id}
                timer={runningTimers[selectedTimerIndex]}
              />
            )}
            {runningTimers.length === 0 && (
              <div className="text-gray-500 text-center flex text-sm bg-gray-100 p-20 w-full rounded-lg justify-center items-center">
                <TimerIcon size={24} className="text-gray-500 mr-2" />
                No running timers
              </div>
            )}
          </div>
        </div>
      </div>
    </Draggable>
  );
};
